import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { authStore } from './stores/authStore';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import PermissionRoute from './components/Auth/PermissionRoute';
import MainLayout from './components/Layout/MainLayout';
import { PermissionProvider } from './contexts/PermissionContext';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AcessoNegado from './pages/AcessoNegado';
import Clientes from './pages/Clientes';
import ClienteForm from './pages/Clientes/ClienteForm';
import ClienteDetalhesCompleto from './pages/Clientes/ClienteDetalhesCompleto';
import Contratos from './pages/Contratos';
import ContratoForm from './pages/Contratos/ContratoForm';
import ContratoDetalhes from './pages/Contratos/ContratoDetalhes';
import DiagnosticoConexao from './pages/Contratos/DiagnosticoConexao';
import ReportBuilder from './components/Reports/ReportBuilder';

// Lazy load other modules
const Planos = React.lazy(() => import('./pages/Planos'));
const PlanosForm = React.lazy(() => import('./pages/Planos/PlanosForm'));
const Vouchers = React.lazy(() => import('./pages/vouchers/index'));
const VouchersForm = React.lazy(() => import('./pages/vouchers/VouchersForm'));
const RelatorioFidelizacoes = React.lazy(() => import('./pages/Relatorios/RelatorioFidelizacoes'));
const Promocoes = React.lazy(() => import('./pages/promocoes/index'));
const PromocoesForm = React.lazy(() => import('./pages/promocoes/PromocoesForm'));
const Boletos = React.lazy(() => import('./pages/Boletos'));
const BoletosForm = React.lazy(() => import('./pages/Boletos/BoletosForm'));
const Chamados = React.lazy(() => import('./pages/Chamados/index'));
const ChamadosTiposForm = React.lazy(() => import('./pages/chamados-tipos/ChamadosTiposForm'));
const Tarefas = React.lazy(() => import('./pages/Tarefas/index'));
const TarefaForm = React.lazy(() => import('./pages/Tarefas/TarefaForm'));
const ChamadosTipos = React.lazy(() => import('./pages/chamados-tipos/index'));
const Atendimentos = React.lazy(() => import('./pages/Atendimentos/index'));
const AtendimentoForm = React.lazy(() => import('./pages/Atendimentos/AtendimentoForm'));
const AtendimentosTipos = React.lazy(() => import('./pages/Atendimentos-tipos/index'));
const AtendimentosTiposForm = React.lazy(() => import('./pages/Atendimentos-tipos/AtendimentosTiposForm'));
const ChamadoForm = React.lazy(() => import('./pages/Chamados/ChamadoForm'));
const OrdensServicoForm = React.lazy(() => import('./pages/OrdensServico/OrdemServicoForm'));
const OrdensServico = React.lazy(() => import('./pages/OrdensServico/index'));
const TiposOS = React.lazy(() => import('./pages/tipos-os/index'));
const TiposOSForm = React.lazy(() => import('./pages/tipos-os/TiposOSForm'));
const Franquias = React.lazy(() => import('./pages/Franquias'));
const FranquiasForm = React.lazy(() => import('./pages/Franquias/FranquiasForm'));
const Usuarios = React.lazy(() => import('./pages/Usuarios/Usuarios'));
const UsuariosForm = React.lazy(() => import('./pages/Usuarios/UsuariosForm'));
const Fornecedores = React.lazy(() => import('./pages/Forncecedores/index'));
const FornecedoresForm = React.lazy(() => import('./pages/Forncecedores/FornecedoresForm'));
const CategoriasFinanceiras = React.lazy(() => import('./pages/categorias-financeiras/index'));
const CategoriasFinanceirasForm = React.lazy(() => import('./pages/categorias-financeiras/CategoriasForm'));
const ContasBancarias = React.lazy(() => import('./pages/contas-bancarias/index'));
const ContasBancariasForm = React.lazy(() => import('./pages/contas-bancarias/ContasBancariasForm'));
const Movimentacoes = React.lazy(() => import('./pages/movimentacoes/index'));
const MovimentacoesForm = React.lazy(() => import('./pages/movimentacoes/MovimentacoesForm'));
const Cidades = React.lazy(() => import('./pages/Cidades/index'));
const CidadesForm = React.lazy(() => import('./pages/Cidades/CidadesForm'));
const Empresas = React.lazy(() => import('./pages/Empresas/index'));
const EmpresasForm = React.lazy(() => import('./pages/Empresas/EmpresasForm'));
const Cargos = React.lazy(() => import('./pages/Cargos/index'));
const CargosForm = React.lazy(() => import('./pages/Cargos/CargosForm'));
const Logs = React.lazy(() => import('./pages/Logs/index'));
const Equipamentos = React.lazy(() => import('./pages/Equipamentos/index'));
const EquipamentosForm = React.lazy(() => import('./pages/Equipamentos/EquipamentosForm'));
const TiposEquipamento = React.lazy(() => import('./pages/Tipos-Equipamento/index'));
const TiposEquipamentoForm = React.lazy(() => import('./pages/Tipos-Equipamento/TiposEquipamentoForm'));
const Fabricantes = React.lazy(() => import('./pages/Fabricantes/index'));
const FabricantesForm = React.lazy(() => import('./pages/Fabricantes/FabricantesForm'));
const Estacoes = React.lazy(() => import('./pages/Estacoes/index'));
const EstacoesForm = React.lazy(() => import('./pages/Estacoes/EstacoesForm'));
const OLTs = React.lazy(() => import('./pages/OLTs/index'));
const OLTsForm = React.lazy(() => import('./pages/OLTs/OLTsForm'));
const Caixas = React.lazy(() => import('./pages/Caixas/index'));
const CaixasForm = React.lazy(() => import('./pages/Caixas/CaixasForm'));
const IPs = React.lazy(() => import('./pages/IPs/index'));
const IPsForm = React.lazy(() => import('./pages/IPs/IPsForm'));
const VLANs = React.lazy(() => import('./pages/VLANs/index'));
const VLANsForm = React.lazy(() => import('./pages/VLANs/VLANsForm'));
const Profile = React.lazy(() => import('./pages/Profile'));
const ServicosAdicionais = React.lazy(() => import('./pages/servicos-adicionais/index'));
const ServicosAdicionaisForm = React.lazy(() => import('./pages/servicos-adicionais/ServicosAdicionaisForm'));
const AssinaContrato = React.lazy(() => import('./pages/Contratos/AssinaContrato'));

// Infraestrutura de Fibra Óptica
const Slots = React.lazy(() => import('./pages/Slots/index'));
const SlotForm = React.lazy(() => import('./pages/Slots/SlotForm'));
const PONs = React.lazy(() => import('./pages/PONs/index'));
const PONForm = React.lazy(() => import('./pages/PONs/PONForm'));
const CaixasEmenda = React.lazy(() => import('./pages/CaixasEmenda/index'));
const CaixaEmendaForm = React.lazy(() => import('./pages/CaixasEmenda/CaixaEmendaForm'));
const CaixasDistribuicao = React.lazy(() => import('./pages/CaixasDistribuicao/index'));
const CaixaDistribuicaoForm = React.lazy(() => import('./pages/CaixasDistribuicao/CaixaDistribuicaoForm'));
const FibrasEmenda = React.lazy(() => import('./pages/FibrasEmenda/index'));
const FibraEmendaForm = React.lazy(() => import('./pages/FibrasEmenda/FibraEmendaForm'));
const Splitters = React.lazy(() => import('./pages/Splitters/index'));
const SplitterForm = React.lazy(() => import('./pages/Splitters/SplitterForm'));
const CabosFibra = React.lazy(() => import('./pages/CabosFibra/index'));
const CaboFibraForm = React.lazy(() => import('./pages/CabosFibra/CaboFibraForm'));
const TubosLoose = React.lazy(() => import('./pages/TubosLoose/index'));
const TuboLooseForm = React.lazy(() => import('./pages/TubosLoose/TuboLooseForm'));
const FibrasCabo = React.lazy(() => import('./pages/FibrasCabo/index'));
const FibraCaboForm = React.lazy(() => import('./pages/FibrasCabo/FibraCaboForm'));
const NetworkMap = React.lazy(() => import('./pages/Mapa-Infra/NetworkMap'));
const ModulosSistema = React.lazy(() => import('./pages/Modulos/ModulosSistema'));
const ModulosSistemaForm = React.lazy(() => import('./pages/Modulos/ModulosSistemaForm'));
const Acoes = React.lazy(() => import('./pages/Acoes/Acoes'));
const AcoesForm = React.lazy(() => import('./pages/Acoes/AcoesForm'));


function App() {
  const { isAuthenticated } = authStore();

  return (
    <Router>
      <PermissionProvider>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              style: {
                background: '#10b981',
              },
            },
            error: {
              style: {
                background: '#ef4444',
              },
            },
          }}
        />
        
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to="/" /> : <Login />} 
          />

          {/* Página pública de assinatura de contrato */}
          <Route path="/assinacontrato/:id" element={
            <React.Suspense fallback={<LoadingSpinner />}>
              <AssinaContrato />
            </React.Suspense>
          } />

          {/* Página de Acesso Negado */}
          <Route 
            path="/acesso-negado" 
            element={
              <ProtectedRoute>
                <AcessoNegado />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            
            {/* Clientes */}
            <Route 
              path="clientes" 
              element={
                <PermissionRoute module="clientes" action="view">
                  <Clientes />
                </PermissionRoute>
              } 
            />
            <Route 
              path="clientes/novo" 
              element={
                <PermissionRoute module="clientes" action="create">
                  <ClienteForm />
                </PermissionRoute>
              } 
            />
            <Route path="clientes/:id/editar" element={
              <PermissionRoute module="clientes" action="edit">
                <ClienteForm />
              </PermissionRoute>
            } />
            <Route path="clientes/:id/detalhes" element={
              <PermissionRoute module="clientes" action="view">
                <ClienteDetalhesCompleto />
              </PermissionRoute>
            } />

            {/* Contratos */}
            <Route path="contratos" element={
              <PermissionRoute module="contratos" action="view">
                <Contratos />
              </PermissionRoute>
            } />
            <Route path="contratos/novo" element={
              <PermissionRoute module="contratos" action="create">
                <ContratoForm />
              </PermissionRoute>
            } />
            <Route path="contratos/:id/editar" element={
              <PermissionRoute module="contratos" action="edit">
                <ContratoForm />
              </PermissionRoute>
            } />
            <Route path="contratos/:id/diagnostico-conexao" element={
              <PermissionRoute module="contratos" action="view">
                <DiagnosticoConexao />
              </PermissionRoute>
            } />
            <Route path="contratos/:id" element={
              <PermissionRoute module="contratos" action="view">
                <ContratoDetalhes />
              </PermissionRoute>
            } />

            {/* Franquias */}
            <Route path="franquias/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="franquias" action="view">
                      <Franquias />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="franquias" action="create">
                      <FranquiasForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="franquias" action="edit">
                      <FranquiasForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Usuários */}
            <Route path="usuarios/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="usuarios" action="view">
                      <Usuarios />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="usuarios" action="create">
                      <UsuariosForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="usuarios" action="edit">
                      <UsuariosForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Profile Routes - Sem permissão, todos podem ver */}
            <Route path="profile" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Profile />
              </React.Suspense>
            } />
            <Route path="profile/:userId" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Profile />
              </React.Suspense>
            } />
            
            {/* Relatórios */}
            <Route path="relatorios" element={
              <PermissionRoute module="relatorios" action="view">
                <ReportBuilder />
              </PermissionRoute>
            } />
            <Route path="relatorios/fidelizacoes" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <PermissionRoute module="relatorios" action="view">
                  <RelatorioFidelizacoes />
                </PermissionRoute>
              </React.Suspense>
            } />

            {/* Atendimentos */}
            <Route path="atendimentos/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="atendimentos" action="view">
                      <Atendimentos />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="atendimentos" action="create">
                      <AtendimentoForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="atendimentos" action="edit">
                      <AtendimentoForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Tipos de Atendimento */}
            <Route path="tipos-atendimento/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="tipos_atendimento" action="view">
                      <AtendimentosTipos />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="tipos_atendimento" action="create">
                      <AtendimentosTiposForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="tipos_atendimento" action="edit">
                      <AtendimentosTiposForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Chamados */}
            <Route path="chamados/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="chamados" action="view">
                      <Chamados />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="chamados" action="create">
                      <ChamadoForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="chamados" action="edit">
                      <ChamadoForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Tipos de Chamado */}
            <Route path="tipos-chamado/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="tipos_chamado" action="view">
                      <ChamadosTipos />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="tipos_chamado" action="create">
                      <ChamadosTiposForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="tipos_chamado" action="edit">
                      <ChamadosTiposForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Tarefas */}
            <Route path="tarefas/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="tarefas" action="view">
                      <Tarefas />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="tarefas" action="create">
                      <TarefaForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="tarefas" action="edit">
                      <TarefaForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Ordens de Serviço */}
            <Route path="ordens-servico/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="ordens_servico" action="view">
                      <OrdensServico />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="ordens_servico" action="create">
                      <OrdensServicoForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="ordens_servico" action="edit">
                      <OrdensServicoForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Tipos de OS */}
            <Route path="tipos-os/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="tipos_os" action="view">
                      <TiposOS />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="tipos_os" action="create">
                      <TiposOSForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="tipos_os" action="edit">
                      <TiposOSForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Planos */}
            <Route path="planos/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="planos" action="view">
                      <Planos />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="planos" action="create">
                      <PlanosForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="planos" action="edit">
                      <PlanosForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Vouchers */}
            <Route path="vouchers/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="vouchers" action="view">
                      <Vouchers />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="vouchers" action="create">
                      <VouchersForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="vouchers" action="edit">
                      <VouchersForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Promoções */}
            <Route path="promocoes/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="promocoes" action="view">
                      <Promocoes />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="promocoes" action="create">
                      <PromocoesForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="promocoes" action="edit">
                      <PromocoesForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Boletos */}
            <Route path="boletos/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="boletos" action="view">
                      <Boletos />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="boletos" action="create">
                      <BoletosForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="boletos" action="edit">
                      <BoletosForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Fornecedores */}
            <Route path="fornecedores/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="fornecedores" action="view">
                      <Fornecedores />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="fornecedores" action="create">
                      <FornecedoresForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="fornecedores" action="edit">
                      <FornecedoresForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Categorias Financeiras */}
            <Route path="categorias-financeiras/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="categorias_financeiras" action="view">
                      <CategoriasFinanceiras />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="categorias_financeiras" action="create">
                      <CategoriasFinanceirasForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="categorias_financeiras" action="edit">
                      <CategoriasFinanceirasForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Contas Bancárias */}
            <Route path="contas-bancarias/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="contas_bancarias" action="view">
                      <ContasBancarias />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="contas_bancarias" action="create">
                      <ContasBancariasForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="contas_bancarias" action="edit">
                      <ContasBancariasForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Movimentações */}
            <Route path="movimentacoes/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="movimentacoes" action="view">
                      <Movimentacoes />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="movimentacoes" action="create">
                      <MovimentacoesForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="movimentacoes" action="edit">
                      <MovimentacoesForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Cidades */}
            <Route path="cidades/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="cidades" action="view">
                      <Cidades />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="cidades" action="create">
                      <CidadesForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="cidades" action="edit">
                      <CidadesForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Empresas */}
            <Route path="empresas/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="empresas" action="view">
                      <Empresas />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="empresas" action="create">
                      <EmpresasForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="empresas" action="edit">
                      <EmpresasForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Cargos */}
            <Route path="cargos/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="cargos" action="view">
                      <Cargos />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="cargos" action="create">
                      <CargosForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="cargos" action="edit">
                      <CargosForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Módulos do Sistema */}
            <Route path="modulos-sistema/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="modulos" action="view">
                      <ModulosSistema />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="modulos" action="create">
                      <ModulosSistemaForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="modulos" action="edit">
                      <ModulosSistemaForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Ações do Sistema */}
            <Route path="acoes/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="acoes" action="view">
                      <Acoes />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="acoes" action="create">
                      <AcoesForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="acoes" action="edit">
                      <AcoesForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Logs */}
            <Route path="logs" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <PermissionRoute module="logs" action="view">
                  <Logs />
                </PermissionRoute>
              </React.Suspense>
            } />
            
            {/* Equipamentos */}
            <Route path="equipamentos/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="equipamentos" action="view">
                      <Equipamentos />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="equipamentos" action="create">
                      <EquipamentosForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="equipamentos" action="edit">
                      <EquipamentosForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Tipos de Equipamento */}
            <Route path="tipos-equipamento/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="tipos_equipamento" action="view">
                      <TiposEquipamento />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="tipos_equipamento" action="create">
                      <TiposEquipamentoForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="tipos_equipamento" action="edit">
                      <TiposEquipamentoForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Fabricantes */}
            <Route path="fabricantes/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="fabricantes" action="view">
                      <Fabricantes />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="fabricantes" action="create">
                      <FabricantesForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="fabricantes" action="edit">
                      <FabricantesForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Estações */}
            <Route path="estacoes/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="estacoes" action="view">
                      <Estacoes />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="estacoes" action="create">
                      <EstacoesForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="estacoes" action="edit">
                      <EstacoesForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* OLTs */}
            <Route path="olts/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="olts" action="view">
                      <OLTs />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="olts" action="create">
                      <OLTsForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="olts" action="edit">
                      <OLTsForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* Caixas */}
            <Route path="caixas/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="caixas" action="view">
                      <Caixas />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="caixas" action="create">
                      <CaixasForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="caixas" action="edit">
                      <CaixasForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* IPs */}
            <Route path="ips/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="ips" action="view">
                      <IPs />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="ips" action="create">
                      <IPsForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="ips" action="edit">
                      <IPsForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
            {/* VLANs */}
            <Route path="vlans/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="vlans" action="view">
                      <VLANs />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="vlans" action="create">
                      <VLANsForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="vlans" action="edit">
                      <VLANsForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Serviços Adicionais */}
            <Route path="servicos-adicionais/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="servicos_adicionais" action="view">
                      <ServicosAdicionais />
                    </PermissionRoute>
                  } />
                  <Route path="novo" element={
                    <PermissionRoute module="servicos_adicionais" action="create">
                      <ServicosAdicionaisForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/editar" element={
                    <PermissionRoute module="servicos_adicionais" action="edit">
                      <ServicosAdicionaisForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Infraestrutura de Fibra Óptica - Slots */}
            <Route path="slots/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="slots" action="view">
                      <Slots />
                    </PermissionRoute>
                  } />
                  <Route path="new" element={
                    <PermissionRoute module="slots" action="create">
                      <SlotForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/edit" element={
                    <PermissionRoute module="slots" action="edit">
                      <SlotForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/view" element={
                    <PermissionRoute module="slots" action="view">
                      <SlotForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Infraestrutura de Fibra Óptica - PONs */}
            <Route path="pons/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="pons" action="view">
                      <PONs />
                    </PermissionRoute>
                  } />
                  <Route path="new" element={
                    <PermissionRoute module="pons" action="create">
                      <PONForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/edit" element={
                    <PermissionRoute module="pons" action="edit">
                      <PONForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/view" element={
                    <PermissionRoute module="pons" action="view">
                      <PONForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Infraestrutura de Fibra Óptica - Caixas de Emenda */}
            <Route path="caixas-emenda/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="caixas_emenda" action="view">
                      <CaixasEmenda />
                    </PermissionRoute>
                  } />
                  <Route path="new" element={
                    <PermissionRoute module="caixas_emenda" action="create">
                      <CaixaEmendaForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/edit" element={
                    <PermissionRoute module="caixas_emenda" action="edit">
                      <CaixaEmendaForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/view" element={
                    <PermissionRoute module="caixas_emenda" action="view">
                      <CaixaEmendaForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Infraestrutura de Fibra Óptica - Caixas de Distribuição */}
            <Route path="caixas-distribuicao/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="caixas_distribuicao" action="view">
                      <CaixasDistribuicao />
                    </PermissionRoute>
                  } />
                  <Route path="new" element={
                    <PermissionRoute module="caixas_distribuicao" action="create">
                      <CaixaDistribuicaoForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/edit" element={
                    <PermissionRoute module="caixas_distribuicao" action="edit">
                      <CaixaDistribuicaoForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/view" element={
                    <PermissionRoute module="caixas_distribuicao" action="view">
                      <CaixaDistribuicaoForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Infraestrutura de Fibra Óptica - Fibras de Emenda */}
            <Route path="fibras-emenda/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="fibras_emenda" action="view">
                      <FibrasEmenda />
                    </PermissionRoute>
                  } />
                  <Route path="new" element={
                    <PermissionRoute module="fibras_emenda" action="create">
                      <FibraEmendaForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/edit" element={
                    <PermissionRoute module="fibras_emenda" action="edit">
                      <FibraEmendaForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/view" element={
                    <PermissionRoute module="fibras_emenda" action="view">
                      <FibraEmendaForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Infraestrutura de Fibra Óptica - Splitters */}
            <Route path="splitters/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="splitters" action="view">
                      <Splitters />
                    </PermissionRoute>
                  } />
                  <Route path="new" element={
                    <PermissionRoute module="splitters" action="create">
                      <SplitterForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/edit" element={
                    <PermissionRoute module="splitters" action="edit">
                      <SplitterForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/view" element={
                    <PermissionRoute module="splitters" action="view">
                      <SplitterForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Infraestrutura de Fibra Óptica - Cabos de Fibra */}
            <Route path="cabos-fibra/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="cabos_fibra" action="view">
                      <CabosFibra />
                    </PermissionRoute>
                  } />
                  <Route path="new" element={
                    <PermissionRoute module="cabos_fibra" action="create">
                      <CaboFibraForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/edit" element={
                    <PermissionRoute module="cabos_fibra" action="edit">
                      <CaboFibraForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/view" element={
                    <PermissionRoute module="cabos_fibra" action="view">
                      <CaboFibraForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Infraestrutura de Fibra Óptica - Tubos Loose */}
            <Route path="tubos-loose/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="tubos_loose" action="view">
                      <TubosLoose />
                    </PermissionRoute>
                  } />
                  <Route path="new" element={
                    <PermissionRoute module="tubos_loose" action="create">
                      <TuboLooseForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/edit" element={
                    <PermissionRoute module="tubos_loose" action="edit">
                      <TuboLooseForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/view" element={
                    <PermissionRoute module="tubos_loose" action="view">
                      <TuboLooseForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Infraestrutura de Fibra Óptica - Fibras de Cabo */}
            <Route path="fibras-cabo/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="fibras_cabo" action="view">
                      <FibrasCabo />
                    </PermissionRoute>
                  } />
                  <Route path="new" element={
                    <PermissionRoute module="fibras_cabo" action="create">
                      <FibraCaboForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/edit" element={
                    <PermissionRoute module="fibras_cabo" action="edit">
                      <FibraCaboForm />
                    </PermissionRoute>
                  } />
                  <Route path=":id/view" element={
                    <PermissionRoute module="fibras_cabo" action="view">
                      <FibraCaboForm />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />

            {/* Mapa de Infraestrutura */}
            <Route path="mapa-infra/*" element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route index element={
                    <PermissionRoute module="mapa_infra" action="view">
                      <NetworkMap />
                    </PermissionRoute>
                  } />
                </Routes>
              </React.Suspense>
            } />
            
          </Route>
          
          {/* 404 */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </PermissionProvider>
    </Router>
  );
}

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-96">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
  </div>
);

export default App;